import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Papa } from 'ngx-papaparse';

@Component({
  selector: 'app-csv-download',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatIconModule, NgClass],
  template: `
    <button
      type="button"
      class="btn-secondary"
      [ngClass]="compact() ? 'h-9! px-4! text-xs!' : ''"
      [disabled]="disabled() || isProcessing()"
      [attr.aria-label]="isProcessing() ? 'Exporting data' : ariaLabel()"
      (click)="downloadCSV()"
    >
      @if (isProcessing()) {
        <svg
          class="mr-2 size-4 animate-spin text-black!"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Exporting...
      } @else {
        <mat-icon aria-hidden="true">download</mat-icon>
        @if (!iconOnly()) {
          <span>Export</span>
        }
      }
    </button>
  `,
})
export class CSVDownloadComponent<T extends object = object> {
  readonly csvData = input.required<T[]>();

  readonly disabled = input<boolean>(false);

  readonly fileName = input.required<string>();

  readonly iconOnly = input<boolean>(false);

  readonly compact = input<boolean>(false);

  readonly ariaLabel = input<string>('Export CSV');

  private readonly papa = inject(Papa);

  readonly isProcessing = signal(false);

  downloadCSV(): void {
    this.isProcessing.set(true);
    setTimeout(() => {
      const csvBlob = new Blob([this.papa.unparse(this.csvData() as any)], {
        type: 'text/csv;charset=utf-8;',
      });
      const csvURL = URL.createObjectURL(csvBlob);
      const tempLink = document.createElement('a');
      tempLink.href = csvURL;
      tempLink.setAttribute('download', this.fileName());
      tempLink.click();
      URL.revokeObjectURL(csvURL);
      this.isProcessing.set(false);
    });
  }
}
